import React, { useCallback, useContext, useMemo } from 'react'

import { getSavedDetailsForOffer, IS_SAVED_COPY, NOT_SAVED_COPY } from './utils'
import BaseBookmarkButton from '../Common/BaseBookmarkButton'
import SaveModal from '../Common/SaveModal'

import {
  saveToTripEvent,
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import AnalyticsPageContext from 'contexts/Analytics/analyticsPageContext'
import ExperienceOfferPageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { RecommendationSaveClickTrackerContext } from 'contexts/RecommendationSaveClickTrackerContext'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { useCurrentExperiencePageTicketOptions } from 'hooks/ExperiencePage/useCurrentExperiencePageTicketOptions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import { MAX_TRIP_NAME_CHARS } from 'tripPlanner/config'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import {
  useCreateTripItemBatch,
  useDeleteTripItem,
  useEditableTrips,
  useTrip,
} from 'tripPlanner/hooks/api'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import {
  setCurrentSectionId,
  setTripItemHasJustBeenAdded,
} from 'tripPlanner/reducers/actions'
import {
  getImmersiveTripId,
  selectTripPlannerTemplateId,
  selectTripPlannerTemplateItemId,
} from 'tripPlanner/selectors'
import {
  BasicTrip,
  CreateItemsBatchResponse,
  FullTrip,
} from 'tripPlanner/types/common'
import { createExperienceItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/experience'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import { getUniqueTripName } from 'tripPlanner/utils/tripName'

export interface Props {
  offer: App.ExperienceOffer
}

const trackTripLoginModalSignUpView = () => {
  fireInteractionEvent(tripLoginModalSignUpView('save'))
}
const trackTripLoginModalSignUpDismiss = () => {
  fireInteractionEvent(tripLoginModalSignUpDismiss('save'))
}

function ExperienceBookmarkButton({ offer }: Props) {
  const inTripId = useTripId()
  const currentTripId = useAppSelector(getImmersiveTripId) ?? inTripId
  const { data } = useEditableTrips()
  const trips = data as BasicTrip[]
  const analyticsPage = useContext(AnalyticsPageContext)

  const { data: currentTrip } = useTrip({ tripId: currentTripId })
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const details = getSavedDetailsForOffer(trips, offer.id)
  const isSaved = details.length > 0

  const recommendationSaveClickTracker = useContext(
    RecommendationSaveClickTrackerContext,
  )

  const {
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
  } = useBookmarkSnackbarHandlers()

  const trackEvent = useTrackExperienceOfferEvent()

  const { mutate: deleteBookmark, isLoading: isDeleting } = useDeleteTripItem({
    onSuccess: (_res, vars, context) => {
      // After item deletion, the basic trip in the RA cache will have enough valid information to provide to handlers
      const trip = context as BasicTrip
      showRemoveSuccessSnackbar(trip.id, trip.name)
    },
    onError: (_res, _vars, context) => {
      // After item deletion, the basic trip in the RA cache will have enough valid information to provide to handlers
      const trip = context as BasicTrip
      showRemoveErrorSnackbar(trip.name)
    },
  })

  const experienceOfferPageState = useContext(ExperienceOfferPageStateContext)

  const allTicketOptions = useCurrentExperiencePageTicketOptions(
    offer.id,
    experienceOfferPageState,
  )

  const showModal = useContext(ModalContext)

  const newTripName = useMemo(() => {
    const name = getUniqueTripName(
      `${offer.location.name} Trip`,
      trips,
      MAX_TRIP_NAME_CHARS,
    )
    return name.length <= MAX_TRIP_NAME_CHARS ? name : ''
  }, [trips, offer])

  const onSuccessCreate = useCallback(
    (res: CreateItemsBatchResponse) => {
      showSaveSuccessSnackbar(res.id, res.name, res.updatedItemIds[0])
      const itemId = res.updatedItemIds[0]
      dispatch(setCurrentSectionId(itemSectionId(itemId)))
      dispatch(setTripItemHasJustBeenAdded())
    },
    [dispatch, showSaveSuccessSnackbar],
  )
  const { mutate: createItem, isLoading: isCreating } = useCreateTripItemBatch({
    onSuccess: onSuccessCreate,
    onError: (e) => {
      console.error(e)
      showSaveErrorSnackbar(offer.name)
    },
  })

  const templateId = useAppSelector(selectTripPlannerTemplateId)
  const templateItemId = useAppSelector(selectTripPlannerTemplateItemId)

  const createTripItems = useCallback(
    (trip: BasicTrip | FullTrip) => {
      const payload = createExperienceItemPayload({
        experience: offer,
        currentRegionCode,
        currencyCode: currentCurrency,
        experienceOfferPageState,
        allTicketOptions,
        taxExclusivePricing: false, // No taxes/fees for experiences
        templateId,
        templateItemId,
      })
      return createItem({
        tripId: trip.id,
        tripItems: [payload],
      })
    },
    [
      allTicketOptions,
      createItem,
      currentCurrency,
      currentRegionCode,
      experienceOfferPageState,
      offer,
      templateId,
      templateItemId,
    ],
  )

  const onSave = useCallback(() => {
    trackEvent('SAVE_TO_TRIP_BUTTON_CLICKED')
    if (isSaved) {
      Promise.all(
        details.map((detail) =>
          deleteBookmark({
            tripId: detail.trip.id,
            tripItemId: detail.itemId,
          }),
        ),
      )
    } else if (currentTrip) {
      fireInteractionEvent(
        saveToTripEvent('button', 'immersive', 'click', analyticsPage),
      )
      recommendationSaveClickTracker()
      createTripItems(currentTrip)
    } else {
      fireInteractionEvent(
        saveToTripEvent('button', 'global', 'click', analyticsPage),
      )
      showModal(
        <SaveModal
          createTripItems={createTripItems}
          isCreatingItem={isCreating}
          offerType="Experience"
          defaultTripName={newTripName}
          offerImageId={offer.images[0]?.id}
        />,
      )
    }
  }, [
    trackEvent,
    isSaved,
    currentTrip,
    details,
    deleteBookmark,
    createTripItems,
    showModal,
    isCreating,
    newTripName,
    offer.images,
    recommendationSaveClickTracker,
    analyticsPage,
  ])

  const onButtonClick = usePendingLoginHandler(
    onSave,
    'tripPlannerLogin',
    trackTripLoginModalSignUpView,
    trackTripLoginModalSignUpDismiss,
  )

  return (
    <BaseBookmarkButton
      label={isSaved ? IS_SAVED_COPY : NOT_SAVED_COPY}
      isSaved={isSaved}
      onClick={onButtonClick}
      isProcessing={isDeleting || isCreating}
    />
  )
}

export default ExperienceBookmarkButton
